import { UploadFile } from '@mui/icons-material'
import type { ButtonProps } from '@mui/material'
import { Box, Button, Typography } from '@mui/material'
import React, { useCallback, useState } from 'react'
import type { DropzoneOptions, FileRejection } from 'react-dropzone'
import { useDropzone } from 'react-dropzone'
import { useIntl } from 'react-intl'

interface Props extends ButtonProps {
  setFile: React.Dispatch<React.SetStateAction<string | Blob>>
  accept?: DropzoneOptions['accept']
  onRejected?: (rejectedFiles: { file: File; errors: { code: string; message: string }[] }[]) => void
}

const FileUploadField: React.FC<Props> = ({ setFile, accept, onRejected, ...buttonProps }) => {
  const [fileName, setFileName] = useState<string | undefined>()
  const { formatMessage } = useIntl()

  const _onDrop: DropzoneOptions['onDrop'] = acceptedFiles => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0])
      setFileName(acceptedFiles[0].name)
    }
  }
  const onDrop: DropzoneOptions['onDrop'] = useCallback(_onDrop, [setFile])

  const onDropRejected: DropzoneOptions['onDropRejected'] = useCallback(
    (rejections: FileRejection[]) => {
      onRejected?.(rejections.map(r => ({ file: r.file, errors: r.errors })))
    },
    [onRejected],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept, onDropRejected })
  const displayValue = isDragActive
    ? formatMessage({ id: 'reporting.dropFile' })
    : fileName || formatMessage({ id: 'import.uploadExcelFile' })

  return (
    <Box {...getRootProps()}>
      <input {...getInputProps()} data-testid="upload-file" />
      <Button variant="contained" startIcon={<UploadFile />} {...buttonProps}>
        <Typography noWrap variant="button">
          {displayValue}
        </Typography>
      </Button>
    </Box>
  )
}

export default FileUploadField
